import React, { useContext } from 'react'
import { useSelector } from 'react-redux'
import { Form, Field } from 'react-final-form'
import { Button, InputNumber, Input as AntInput, Select as AntSelect, message } from 'antd'
import styled from 'styled-components'
import useAction from 'utils/useAction'
import Modal from 'components/Modal/Modal'
import {
  selectors as gptPromptsSelectors,
  actions as gptPromptsActions,
} from 'domains/gptPrompts'

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const Label = styled.label`
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);

  &.required::after {
    content: ' *';
    color: #ff4d4f;
  }
`

const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
`

const InlineFieldsRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;

  > div {
    flex: 1;
  }
`

const TextAreaWrapper = styled.div`
  .ant-input {
    min-height: 120px;
  }
`

const MODEL_OPTIONS = [
  { label: 'gpt-4', value: 'gpt-4' },
  { label: 'gpt-4o', value: 'gpt-4o' },
  { label: 'gpt-4o-mini', value: 'gpt-4o-mini' },
  { label: 'gpt-5', value: 'gpt-5' },
  { label: 'gpt-5.2', value: 'gpt-5.2' },
]

const required = (value) => (value ? undefined : 'This field is required')

const validateTemperature = (value) => {
  if (value === null || value === undefined || value === '') return undefined
  if (value < 0 || value > 2) return 'Temperature must be between 0 and 2'
  return undefined
}

const validateMaxTokens = (value) => {
  if (value === null || value === undefined || value === '') return undefined
  if (value < 0) return 'Max tokens must be 0 or greater'
  if (!Number.isInteger(value)) return 'Max tokens must be an integer'
  return undefined
}

const TextAreaField = ({ input, meta, label, required: isRequired }) => (
  <FormField>
    <Label className={isRequired ? 'required' : ''}>{label}</Label>
    <TextAreaWrapper>
      <AntInput.TextArea
        {...input}
        rows={6}
        status={meta.touched && meta.error ? 'error' : undefined}
      />
    </TextAreaWrapper>
    {meta.touched && meta.error && (
      <span style={{ color: '#ff4d4f', fontSize: 12 }}>{meta.error}</span>
    )}
  </FormField>
)

const NumberInputField = ({ input, meta, label, min, max, step }) => (
  <FormField>
    <Label>{label}</Label>
    <InputNumber
      {...input}
      min={min}
      max={max}
      step={step}
      style={{ width: '100%' }}
      status={meta.touched && meta.error ? 'error' : undefined}
    />
    {meta.touched && meta.error && (
      <span style={{ color: '#ff4d4f', fontSize: 12 }}>{meta.error}</span>
    )}
  </FormField>
)

const SelectField = ({ input, meta, label, options }) => (
  <FormField>
    <Label>{label}</Label>
    <AntSelect
      {...input}
      style={{ width: '100%' }}
      status={meta.touched && meta.error ? 'error' : undefined}
      options={options}
    />
    {meta.touched && meta.error && (
      <span style={{ color: '#ff4d4f', fontSize: 12 }}>{meta.error}</span>
    )}
  </FormField>
)

const GptPromptForm = () => {
  const modal = useContext(Modal.Context)
  const gptPrompts = useSelector(gptPromptsSelectors.gptPromptsSelector)
  const updateGptPrompt = useAction(gptPromptsActions.updateGptPromptRequest)

  const promptId = modal.data?.id
  const gptPrompt = gptPrompts.find((p) => p.id === promptId)

  if (!gptPrompt) {
    return <div>Loading...</div>
  }

  const initialValues = {
    code: gptPrompt.code,
    prompt: gptPrompt.prompt,
    developer_hint: gptPrompt.developer_hint,
    model: gptPrompt.model,
    temperature: gptPrompt.temperature,
    max_output_tokens: gptPrompt.max_output_tokens,
  }

  return (
    <Form
      onSubmit={(values) => {
        updateGptPrompt({
          id: promptId,
          values,
          callback: () => modal.close(),
        })
      }}
      initialValues={initialValues}
      render={({ handleSubmit, submitting, pristine }) => (
        <StyledForm onSubmit={handleSubmit}>
          <Field name="code">
            {({ input }) => (
              <FormField>
                <Label>Code</Label>
                <AntInput {...input} disabled />
              </FormField>
            )}
          </Field>

          <Field name="prompt" validate={required}>
            {(props) => <TextAreaField {...props} label="Prompt" required />}
          </Field>

          <Field name="developer_hint">
            {(props) => <TextAreaField {...props} label="Developer Hint" />}
          </Field>

          <InlineFieldsRow>
            <Field name="model">
              {(props) => (
                <SelectField
                  {...props}
                  label="Model"
                  options={MODEL_OPTIONS}
                />
              )}
            </Field>

            <Field name="temperature" validate={validateTemperature}>
              {(props) => (
                <NumberInputField
                  {...props}
                  label="Temperature"
                  min={0}
                  max={2}
                  step={0.1}
                />
              )}
            </Field>

            <Field name="max_output_tokens" validate={validateMaxTokens}>
              {(props) => (
                <NumberInputField
                  {...props}
                  label="Max Output Tokens"
                  min={0}
                  step={1}
                />
              )}
            </Field>
          </InlineFieldsRow>

          <ButtonsWrapper>
            <Button onClick={() => modal.close()}>Cancel</Button>
            <Button
              type="primary"
              onClick={handleSubmit}
              loading={submitting}
              disabled={pristine}
            >
              Save
            </Button>
          </ButtonsWrapper>
        </StyledForm>
      )}
    />
  )
}

export default GptPromptForm
